import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BwjSidePanel } from '../../../../framework/components/bwj-side-panel';
import { xlate, xlationContext } from '../../../../framework/utils/i18n';
import * as formService from '../../form-services/account-preferences';
import { BwjButtonType, IBwjButtonProps } from '../../../../framework/components/bwj-button';
import { BwjSingleSelectField } from '../../../../framework/components/bwj-select-field/single';
import { BwjGrid, Col, Row } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { getTheme, IStyle, ITheme, mergeStyleSets, Slider } from '@fluentui/react';
import { FontSizePreference } from '../../../../entities-generated/common/font-size-preference';

xlationContext('Presenter account preferences panel');

interface IPresenterAccountPreferencesPanelProps {
  invokeShowPanel: {} | undefined;
  onSavePreferences: () => void;
}

const getStyleClasses = (theme: ITheme) => {
  const main: IStyle = {
    padding: 20,
  };

  const sampleTextContainer: IStyle = {
    margin: 0,
    marginTop: 5,
    padding: 0,
    position: 'relative',
  };

  const sampleTextBase: IStyle = {
    fontSize: '1em',
    color: theme.palette.neutralSecondaryAlt,
    lineHeight: '1.5em',
    margin: 0,
    padding: 0,
  };

  const sampleText: IStyle = {
    ...sampleTextBase,
    position: 'absolute',
    top: 0,
    left: 0,
  };

  const phantomText: IStyle = {
    ...sampleTextBase,
    opacity: 0,
  };

  return mergeStyleSets({
    main,
    sampleTextContainer,
    sampleText,
    phantomText,
  });
};

const sampleTexts = [
  xlate('Elementary, my dear Watson.'),
  xlate('Parting is such sweet sorrow.'),
  xlate('A good report invigorates the bones.'),
];

const subscriberId = 'Component.PresenterAccountPreferencesPanel';

export const PresenterAccountPreferencesPanel: React.FunctionComponent<IPresenterAccountPreferencesPanelProps> = (props) => {
  const { invokeShowPanel, onSavePreferences } = props;
  const [showPanel, setShowPanel] = useState(false);
  const formModel = formService.useFormModel(subscriberId);
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel);
  const theme = getTheme();
  const styles = getStyleClasses(theme);
  const sampleText = useRef(sampleTexts[(new Date()).getTime() % 3]);

  // on show panel invoked
  useEffect(() => {
    if(!!invokeShowPanel) {
      setShowPanel(true);
    }
  }, [invokeShowPanel]);

  const savePreferences = useCallback(() => {
    setShowPanel(false);
    onSavePreferences();
  }, [onSavePreferences]);

  const onCancel = useCallback(() => {
    if(hasUnsavedChanges) {
      formService.revertChanges();
    }
    setShowPanel(false);
  }, [hasUnsavedChanges]);

  const footerButtons = useMemo(() => {
    const buttons: IBwjButtonProps[] = [];
    if(hasUnsavedChanges) {
      buttons.push({
        buttonStyle: BwjButtonType.PrimaryStandard,
        text: xlate('Save changes'),
        onClick: savePreferences,
      });
    }
    buttons.push({
      buttonStyle: BwjButtonType.DefaultStandard,
      text: xlate('Cancel'),
      onClick: onCancel,
    });
    return buttons;
  }, [hasUnsavedChanges, savePreferences, onCancel]);

  return (
    <BwjSidePanel
      showPanel={showPanel}
      title={xlate('Settings')}
      footerButtons={footerButtons}
      onDismiss={onCancel}
      closeButtonAriaLabel={xlate('Close settings panel')}
    >
      <div className={styles.main}>
        <BwjGrid>
          <Row bottomSpacing={Spacing.Medium}>
            <Col small={12}>
              <BwjSingleSelectField
                id={formService.timeDisplayFormat.id}
                required={false}
                label={formService.timeDisplayFormat.description}
                onUpdate={formService.timeDisplayFormat.onUpdate}
                input={formModel.timeDisplayFormat}
                options={formService.timeDisplayOptions}
                optionDisplayKey={'description'}
                optionValueKey={'value'}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Medium}>
            <Col small={12}>
              <Slider
                label={formService.fontSizePreference.description}
                value={formModel.fontSizePreference}
                onChange={formService.fontSizePreference.onUpdate}
                min={FontSizePreference.PX12}
                max={FontSizePreference.PX24}
                step={2}
                snapToStep={true}
                showValue={false}
              />
              <div
                className={styles.sampleTextContainer}
                style={{ fontSize: formModel.fontSizePreference }}
              >
                <p className={styles.phantomText} style={{ fontSize: FontSizePreference.PX24 }}>{sampleText.current}</p>
                <p className={styles.sampleText}>{sampleText.current}</p>
              </div>
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Medium}>
            <Col small={12}>
              <Slider
                label={formService.warningColorWhenTimeRemainingAt.description}
                value={formModel.warningColorWhenTimeRemainingAt}
                valueFormat={formService.warningColorWhenTimeRemainingAt.format}
                onChange={formService.warningColorWhenTimeRemainingAt.onUpdate}
                min={0}
                max={50}
                step={5}
                snapToStep={true}
                showValue={true}
              />
            </Col>
          </Row>
        </BwjGrid>
      </div>
    </BwjSidePanel>
  );
};
